import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Play, MapPin, Star } from "lucide-react";
import { DashboardCard } from "./DashboardCard";
import { scrollToId } from "./Header";
import { EASE, BottomWave } from "./shared";
import { useLang } from "./i18n";
import { useTheme } from "./theme";

let heroPlayed = false;

export function Hero({ start = true }) {
  const ref = useRef(null);
  const { t, images } = useLang();
  const { theme } = useTheme();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section id="top" ref={ref} className="grain t-bg-deep relative overflow-hidden pb-40 pt-[130px] md:pb-48 lg:pt-[150px]">
      {theme === "dark" && (
        <img
          src={images.heroTexture}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-overlay [filter:hue-rotate(70deg)_saturate(0.85)]"
        />
      )}
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-aqua-500/15 blur-[140px]"
      />
      <div aria-hidden="true" className="only-dark absolute -left-52 bottom-0 h-[480px] w-[480px] rounded-full bg-navy blur-[120px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-aqua-600/30 bg-aqua-600/10 px-4 py-1.5"
          >
            <MapPin size={13} className="text-aqua-600" />
            <span className="text-xs font-semibold tracking-wide text-aqua-700">{t.hero.badge}</span>
          </motion.div>

          <h1 className="t-text font-display text-5xl font-semibold leading-[1.04] tracking-[-0.03em] sm:text-6xl xl:text-7xl">
            {t.hero.lines.map((line, i) => (
              <span key={`${i}-${line}`} className="-mb-1 block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-maple" : ""}`}
                  initial={{ y: "112%" }}
                  animate={start ? { y: 0 } : {}}
                  transition={{ duration: heroPlayed ? 0.6 : 0.9, delay: heroPlayed ? i * 0.07 : 0.1 + i * 0.12, ease: EASE }}
                  onAnimationComplete={() => {
                    heroPlayed = true;
                  }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5, duration: 0.7, ease: EASE }}
            className="t-text-muted mt-7 max-w-lg text-base leading-relaxed md:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.65, duration: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-quote-btn"
              onClick={() => scrollToId("#quote")}
              className="group flex items-center gap-2 rounded-full bg-maple px-7 py-3.5 text-sm font-bold text-white shadow-[0_12px_36px_rgba(217,58,43,0.35)] transition-all duration-300 hover:bg-[#C23225] hover:shadow-[0_16px_44px_rgba(217,58,43,0.45)]"
            >
              {t.hero.quoteCta}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-demo-btn"
              onClick={() => scrollToId("#how-it-works")}
              className="t-ghost-btn group flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-navy/5 transition-colors duration-300 group-hover:bg-maple group-hover:text-white">
                <Play size={11} className="ml-0.5" />
              </span>
              {t.hero.demoCta}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8, duration: 0.7, ease: EASE }}
            className="mt-14 flex flex-wrap gap-x-12 gap-y-6"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="t-text flex items-center gap-1.5 font-display text-3xl font-semibold">
                  {s.value}
                  {i === 2 && <Star size={16} className="fill-maple text-maple" />}
                </div>
                <div className="t-text-muted mt-1 text-xs font-medium uppercase tracking-[0.15em]">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.9, ease: EASE }}
          className="relative"
          style={{ perspective: 1400, y: cardY }}
          onMouseMove={onMove}
          onMouseLeave={() => {
            mx.set(0.5);
            my.set(0.5);
          }}
        >
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
            <DashboardCard start={start} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={start ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 1.1, duration: 0.7, ease: EASE }}
            className="absolute -left-4 top-10 sm:-left-10"
          >
            <motion.div
              animate={{ y: [0, -9, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              data-testid="ai-alert-chip"
              className="flex items-center gap-2.5 rounded-full bg-white/95 py-2.5 pl-3 pr-5 shadow-[0_16px_48px_rgba(0,0,0,0.25)] backdrop-blur"
            >
              <span className="pulse-dot h-2 w-2 rounded-full bg-maple" />
              <span className="text-xs font-bold text-ink">{t.hero.chip}</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <BottomWave fill="#F7F9FB" />
    </section>
  );
}
